import { useLingui } from "@lingui/react";
import { Button } from "@warp-ds/elements/react/button";
import { type FC, type ReactNode, type UIEventHandler, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import ReactFocusLock from "react-focus-lock";
import { useImages } from "../../context/DealerDataProviderContext.tsx";
import { useGalleryState } from "../../context/GalleryStateProviderContext.tsx";
import { Icon } from "../Components/Icon.tsx";
import { distanceToCurrentImage, generateSrcSet, getVisibleImageIndex, useDebounce } from "../Utils/galleryUtils.ts";
import "./image-gallery.css";

const FocusLock = ReactFocusLock as unknown as FC<{ children: ReactNode; returnFocus: boolean }>;

export const ImageGalleryFullscreen = () => {
  const { i18n } = useLingui();
  const { showNextImage, showPreviousImage, setFullScreen, currentImageIndex, setImageIndex, isScrolling } =
    useGalleryState();
  const images = useImages();
  const listRef = useRef<HTMLUListElement>(null);
  const debounce = useDebounce();

  useEffect(() => {
    document.body.classList.add("body-scroll-lock");

    return () => {
      document.body.classList.remove("body-scroll-lock");
    };
  }, []);

  useEffect(() => {
    const currentEl = document.getElementById(`gallery-image-${currentImageIndex}`) as HTMLImageElement | null;
    currentEl?.focus();

    if (listRef.current) {
      const visibleIndex = getVisibleImageIndex(listRef, images);
      if (visibleIndex !== -1 && visibleIndex !== currentImageIndex) {
        document
          .getElementById(`gallery-image-${currentImageIndex}`)
          ?.scrollIntoView({ block: "nearest", behavior: "instant" });
      }
    }
  }, [currentImageIndex, images]);

  const onScroll: UIEventHandler<HTMLUListElement> = () => {
    debounce(() => {
      isScrolling.current = false;

      if (listRef.current) {
        const visibleIndex = getVisibleImageIndex(listRef, images);
        if (visibleIndex !== -1 && visibleIndex !== currentImageIndex) {
          setImageIndex(visibleIndex);
        }
      }
    }, 50);
  };

  const ariaLabel = useMemo(() => {
    const currentImage = images[currentImageIndex];
    let label = "";
    if (currentImage?.altText) {
      label += `${currentImage.altText}${currentImage.altText?.endsWith(".") ? " " : ". "}`;
    }
    label += i18n.t({
      id: "dealerBrandingLayout.imageGallery.description",
      message: "Image {current} of {total}",
      values: { current: currentImageIndex + 1, total: images.length },
    });
    return label;
  }, [images, currentImageIndex, i18n]);

  return createPortal(
    <FocusLock returnFocus={true}>
      {/** biome-ignore lint/a11y/useFocusableInteractive: button descendant */}
      {/** biome-ignore lint/a11y/useKeyWithClickEvents: button descendant */}
      {/** biome-ignore lint/a11y/useSemanticElements: button descendant */}
      <div
        role="button"
        data-testid="image-gallery-fullscreen"
        aria-label={i18n.t({ id: "dealerBrandingLayout.imageGallery.next", message: "Go to next image" })}
        className="fixed w-full h-full z-20 top-0 left-0 flex flex-col bg-[--w-black/90]"
        onClick={showNextImage}
      >
        <div className="flex justify-end m-8">
          <Button
            aria-label={i18n.t({ id: "dealerBrandingLayout.imageGallery.fullscreen.close", message: "Close" })}
            variant="quiet"
            className="part-[base]:!s-text-inverted-static part-[base]:!hover:s-text-placeholder part-[base]:!hover:bg-transparent"
            onClick={() => setFullScreen(false)}
          >
            <Icon name="Close" size="medium" className="block" />
          </Button>
        </div>
        <div className="absolute w-full h-full flex justify-between items-center px-16 pointer-events-none z-20">
          <Button
            variant="pill"
            aria-label={i18n.t({ id: "dealerBrandingLayout.imageGallery.previous", message: "Go to previous image" })}
            className="pointer-events-auto min-w-48 min-h-48 part-[base]:!bg-[--w-black/70] part-[base]:!hover:bg-[--w-black/90] part-[base]:!s-text-inverted-static transition-transform duration-100 ease-in-out part-[base]:active:scale-95"
            onClick={showPreviousImage}
          >
            <Icon name="ChevronLeft" size="medium" />
          </Button>
          <Button
            variant="pill"
            aria-label={i18n.t({ id: "dealerBrandingLayout.imageGallery.next", message: "Go to next image" })}
            className="pointer-events-auto min-w-48 min-h-48 part-[base]:!bg-[--w-black/70] part-[base]:!hover:bg-[--w-black/90] part-[base]:!s-text-inverted-static transition-transform duration-100 ease-in-out part-[base]:active:scale-95"
            onClick={showNextImage}
          >
            <Icon name="ChevronRight" size="medium" />
          </Button>
        </div>
        <div className="absolute text-center bottom-16 flex flex-col justify-center items-center w-full z-20 mb-16">
          <output
            aria-live="polite"
            aria-label={`${images[currentImageIndex].altText} ${ariaLabel}`}
            className="rounded-8 p-8 !bg-[--w-black/70] !s-text-inverted-static truncate mx-16 max-w-full"
          >
            <span aria-hidden>
              {images[currentImageIndex].altText ? `${images[currentImageIndex].altText} ` : null}
            </span>
            <span aria-hidden>{`(${currentImageIndex + 1}/${images.length})`}</span>
          </output>
        </div>
        <section className="relative flex justify-center items-center overflow-hidden h-full z-0 mb-16">
          <h2 className="sr-only">
            {i18n.t({ id: "dealerBrandingLayout.imageGallery.title", message: "Image gallery" })}
          </h2>
          <button
            type="button"
            aria-label={i18n.t({ id: "dealerBrandingLayout.imageGallery.next", message: "Go to next image" })}
            className="w-full h-full bg-transparent"
            onClick={showNextImage}
          >
            <ul
              aria-live="polite"
              aria-label={ariaLabel}
              ref={listRef}
              className="flex hide-scrollbar h-full w-full overflow-x-auto"
              style={{
                scrollSnapType: "x mandatory",
              }}
              onScroll={onScroll}
            >
              {images.map((image, index) => {
                const isCloseToBeingShown = distanceToCurrentImage(currentImageIndex, index, images.length) <= 2;
                const isActive = index === currentImageIndex;
                return (
                  <li
                    style={{
                      scrollSnapAlign: "start",
                      flexShrink: "0",
                    }}
                    className="w-full relative"
                    key={image.url}
                  >
                    <img
                      className="w-full h-full cursor-pointer relative z-10 object-contain"
                      style={{
                        aspectRatio: "3/2",
                        backgroundColor: "transparent",
                      }}
                      id={`gallery-image-${index}`}
                      srcSet={generateSrcSet(image.url)}
                      alt={image.altText}
                      aria-label={image.altText}
                      tabIndex={isActive ? 0 : -1}
                      loading={isCloseToBeingShown ? undefined : "lazy"}
                    />
                  </li>
                );
              })}
            </ul>
          </button>
        </section>
      </div>
    </FocusLock>,
    document.body,
  );
};

ImageGalleryFullscreen.displayName = "ImageGalleryFullscreen";
